/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import handleDates from 'utils/timeDifference';
import { useNavigate } from 'react-router-dom';
import { updatePlanModal, updateNavigationTourGuide } from 'store/user';
import { Space, Typography, Button } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import Joyride, { STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { TRANSACTION_FILTER_LIST } from 'constants/transaction';
import EbzTable from 'components/EbzTable';
import EbzButton from 'components/EbzButton';
import EbzFilterGroup from 'components/EbzFilterGroup';
import transactionsAPI from 'services/transactions';
import merchantAPI from 'services/merchants';
import transactionsBlur from 'assets/images/transactions_table.webp';
import CustomSkeleton from 'components/CustomSkeleton';
import trackingService from 'services/trackingService';
import EbzPopover from 'components/EbzPopover';
import EVENT_NAME from 'utils/const';
import joyrideCustomTooltip from 'components/JoyrideComponent/JoyrideTooltip';
import Alert from 'components/Alert';
import reportsAPI from 'services/reports';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import {
  getTransactionColumns,
  TOUR_TRANSACTION_CONTENT,
  getTransactionSummaryCards,
  getTransactionSearchList
} from './const';
import './index.scss';

function TransactionListPage() {
  // State
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    nextCursor: null,
    prevCursor: null,
    pageNumber: null
  });
  const [filtersObj, setFiltersObj] = useState({});
  const [currentView, setCurrentView] = useState([]);
  const [viewMoreModal, setViewMoreModal] = useState(false);
  const [transactionSummary, setTransactionSummary] = useState({});
  const [isTransactionSummary, setIsTransactionSummary] = useState(false);
  const [tourGuideRun, setTourGuideRun] = useState(false);
  const [tourGuideSteps, setTourGuideSteps] = useState([]);
  const [tourStepIndex, setTourStepIndex] = useState(1);
  const [hasSubmerchantData, setHasSubmerchantData] = useState(false);
  const tourGuide = useSelector((state) => state.user.navigationTourGuidetails);
  const [tableScroll, setTableScroll] = useState({ x: 300, y: 350 });

  const fetchTransactionsSummary = async () => {
    try {
      const response = await transactionsAPI.transaction_summary();
      if (!response.success) {
        console.error(
          'Something went wrong while fetching partner transaction transactionSummary.'
        );
        return;
      }
      setTransactionSummary(response?.data);
    } catch (error) {
      console.error('Error occurred while fetching transactions:', error);
    } finally {
      setIsTransactionSummary(true);
    }
  };

  const isTransacationsAllowed = useSelector(
    (state) => state.user.planPermissions.can_view_transactions
  );
  const productList = useSelector((state) => state?.user?.productList);
  const planModal = useSelector((state) => state.user.planModal);
  const dispatch = useDispatch();
  const togglePlanModal = () => {
    dispatch(updatePlanModal(!planModal));
  };

  const fetchSubmerchantReportList = async () => {
    const response = await reportsAPI.submerchantList();

    const options = [];
    response.data.forEach((e) => {
      options.push({
        value: e.submerchant_id,
        label: e.name,
        email: e.email
      });
    });

    if (Object.keys(options).length > 0) setHasSubmerchantData(true);
  };

  const fetchTransactions = async (type = 'next') => {
    setLoading(true);
    try {
      const newPagination =
        type === 'next'
          ? { next_cursor: pagination?.nextCursor, page_number: pagination?.pageNumber }
          : { prev_cursor: pagination?.prevCursor, page_number: pagination?.pageNumber };

      const response = await transactionsAPI.transactions({
        ...newPagination,
        ...filtersObj
      });

      setTransactions([]);

      if (!response.success) {
        Alert('something went wrong', response?.data?.error?.message, 'error');
        return;
      }
      if (response?.data?.transaction_aggregate_data?.length === 0) {
        setPagination({
          nextCursor: null,
          prevCursor: response?.data?.prev_cursor || null,
          pageNumber: response?.data?.page_number || 1
        });
      }
      setTransactions(response?.data?.transaction_aggregate_data);

      if (response?.data?.transaction_aggregate_data?.length === response?.data?.page_size) {
        setPagination({
          nextCursor: response?.data?.next_cursor,
          prevCursor: response?.data?.prev_cursor,
          pageNumber: response?.data?.page_number
        });
      } else {
        setPagination({
          nextCursor: null,
          prevCursor: response?.data?.prev_cursor,
          pageNumber: response?.data?.page_number
        });
      }
    } catch (error) {
      console.error('Error occurred while fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isTransacationsAllowed) {
      fetchTransactionsSummary();
      fetchTransactions();
      fetchSubmerchantReportList();
    } else {
      navigate('/');
    }
  }, [filtersObj, isTransacationsAllowed]);

  // Handle table pagination and filtering
  const handleTableChange = (filter) => {
    setFiltersObj(filter);
    setPagination((prevState) => ({
      ...prevState,
      nextCursor: null,
      prevCursor: null,
      pageNumber: null
    }));
    fetchTransactions();
  };

  // Apply filters callback function
  const applyFiltersCallback = (checkFilters) => {
    console.log(checkFilters, 'checkFilters');
    const cleanedFilters = checkFilters.filter(
      (item) => item != null && Object.keys(item).length > 0
    );
    const mergedDict = cleanedFilters.reduce(
      (prevObj, currentObj) => ({ ...prevObj, ...currentObj }),
      {}
    );
    console.log(mergedDict);
    setFiltersObj(mergedDict);
    setPagination((prevState) => ({
      ...prevState,
      nextCursor: null,
      prevCursor: null,
      pageNumber: null
    }));
  };

  // Clear filters callback function
  const clearFiltersCallback = () => {
    setFiltersObj({});
    setPagination((prevState) => ({
      ...prevState,
      nextCursor: null,
      prevCursor: null,
      pageNumber: null
    }));
  };

  const toggleModal = () => {
    setViewMoreModal(false);
    setCurrentView(null);
  };

  const handleViewDetails = (transactionInfo) => {
    setViewMoreModal(true);
    setCurrentView(transactionInfo);
  };

  const fetchAndUpdateNavigation = async (paramKey) => {
    const request = {
      update: paramKey
    };
    const response = await merchantAPI.fetchNavigationFlag(request);
    if (response.success) {
      dispatch(updateNavigationTourGuide(response?.data));
    } else {
      console.log('something went wrong');
    }
  };

  const handleJoyrideCallback = (callbackProps) => {
    const { status, index, type, action, size } = callbackProps;

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      if ([ACTIONS.NEXT].includes(action)) {
        const nextIndex = index + 1;
        setTourStepIndex(nextIndex);
      } else if ([ACTIONS.PREV].includes(action) && index === 1) {
        setTourStepIndex(1);
      } else {
        const prevIndex = index - 1;
        setTourStepIndex(prevIndex);
      }
      if (index === 1 && [ACTIONS.PREV].includes(action)) {
        setTourGuideRun(false);
        fetchAndUpdateNavigation('disable_transaction_guide');
      }
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Need to set our running state to false, so we can restart if we click start again.
      setTourGuideRun(false);
    }
    const lastStepIndex = size - index;
    if ([ACTIONS.CLOSE].includes(action) || [EVENTS.TOUR_END].includes(type)) {
      if (lastStepIndex > 1) {
        setTourStepIndex(size - 1);
      } else {
        setTourGuideRun(false);
        fetchAndUpdateNavigation('disable_transaction_guide');
      }
    }
  };

  const columns = getTransactionColumns(handleViewDetails);
  const transactionSummaryCards = getTransactionSummaryCards(transactionSummary);
  const transactionSearchlist = getTransactionSearchList(hasSubmerchantData);

  useEffect(() => {
    if (
      tourGuide?.dashboard_guide?.tour_guide &&
      tourGuide?.dashboard_guide?.dashboard_modules?.disable_transaction_guide
    ) {
      setTourGuideRun(!tourGuideRun);
      setTourGuideSteps(TOUR_TRANSACTION_CONTENT);
    }
  }, [tourGuide]);

  useEffect(() => {
    const updateScroll = () => {
      setTableScroll({
        x: window.innerWidth - 500, // adjust offset as needed
        y: window.innerHeight - 500 // adjust offset as needed
      });
    };

    updateScroll(); // run on mount
    window.addEventListener('resize', updateScroll); // run on resize

    return () => window.removeEventListener('resize', updateScroll); // cleanup
  }, []);

  return (
    <>
      <div className="transaction-main-page">
        <Joyride
          callback={handleJoyrideCallback}
          continuous
          run={tourGuideRun}
          steps={tourGuideSteps}
          scrollOffset={64}
          showSkipButton
          styles={{
            options: {
              primaryColor: 'var(--color-fill-alter)',
              zIndex: 100
            }
          }}
          tooltipComponent={joyrideCustomTooltip}
          stepIndex={tourStepIndex}
        />
        {isTransacationsAllowed ? (
          isTransactionSummary ? (
            <div className="transaction-report-funnel">
              {transactionSummary?.document_updated_at && (
                <div className="last-updated-at-div">
                  <Typography.Text className="last-updated-at-content">
                    Last Updated on {handleDates(transactionSummary?.document_updated_at)}
                  </Typography.Text>
                </div>
              )}

              <div className="main-funnel">
                {transactionSummaryCards.length > 1 &&
                  transactionSummaryCards.map((item) => (
                    <div className="cards">
                      <div className="main-img">
                        <div>{item?.rect}</div>
                        <div className="img-overlap">{item?.icon}</div>
                      </div>
                      <div className="funnel-text-div">
                        <Typography.Text className="title">{item?.label}</Typography.Text>
                        <Typography.Text className="text">{item?.value}</Typography.Text>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <CustomSkeleton />
          )
        ) : null}
        <Space
          direction="horizontal"
          style={{
            width: '100%',
            paddingBottom: '10px',
            justifyContent: 'space-between',
            marginTop: '14px'
          }}
        >
          <Space>
            {isTransacationsAllowed ? (
              <EbzPopover
                placement="bottomLeft"
                content={
                  <EbzFilterGroup
                    searchParams={transactionSearchlist}
                    filters={TRANSACTION_FILTER_LIST}
                    apply={applyFiltersCallback}
                    clear={clearFiltersCallback}
                  />
                }
                trigger="click"
                overlayStyle={{
                  width: '400px'
                }}
                overlayClassName="ebz-filters-popover"
              >
                <EbzButton
                  size="large"
                  onClick={() => {
                    trackingService.trackEvent(EVENT_NAME.BUTTON_CLICK, {
                      name: 'Search & filter'
                    });
                  }}
                  className="ebz-filters-btn"
                >
                  Search & Filter <DownOutlined />
                </EbzButton>
              </EbzPopover>
            ) : (
              <Button type="primary" size="large" disabled>
                Search & Filter <DownOutlined />
              </Button>
            )}
          </Space>
        </Space>
        {isTransacationsAllowed ? (
          <div className="transaction-table-size">
            <EbzTable
              columns={columns}
              rowKey={(r) => `${r?.actual_int_id}_transaction`}
              dataSource={transactions?.length > 0 ? transactions : []}
              loading={loading}
              scroll={
                transactions?.length > 0
                  ? {
                      ...tableScroll,
                      scrollToFirstRowOnChange: true
                    }
                  : { x: 'max-content' }
              }
              onChange={handleTableChange}
              pagination={false}
              size="middle"
              sticky
              thBgColor="#EEEDF1"
              fetchTransactions={fetchTransactions}
              newPagination={pagination}
            />
          </div>
        ) : (
          <div
            style={{
              position: 'relative'
            }}
          >
            <img src={transactionsBlur} alt="CUstom Report Table" style={{ maxWidth: '100%' }} />
            <div
              style={{
                position: 'absolute',
                textAlign: 'center',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            >
              <Typography.Title level={4}>
                Explore trends, search through & export transactions of your referrals from the
                dashboard on the Premium Plan.
              </Typography.Title>
              <Button type="primary" onClick={togglePlanModal}>
                View Plans
              </Button>
            </div>
          </div>
        )}
      </div>
      <TransactionDetailsDrawer
        viewMoreModal={viewMoreModal}
        currentView={currentView}
        productList={productList}
        handleViewDetails={handleViewDetails}
        toggleModal={toggleModal}
      />
    </>
  );
}

export default TransactionListPage;
